/**
 * Settings Layout with Sidebar Navigation
 *
 * Provides a unified sidebar navigation for all settings pages.
 * Responsive: sidebar collapses to top tabs on mobile.
 */

import { useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Menu, theme } from 'antd';
import {
  AppstoreOutlined,
  TagsOutlined,
  CheckSquareOutlined,
  TeamOutlined,
  BgColorsOutlined,
  CloudDownloadOutlined,
  CrownOutlined,
  FolderOutlined,
  RobotOutlined,
  HistoryOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '@store/authStore';
import type { MenuProps } from 'antd';

type MenuItem = Required<MenuProps>['items'][number];

export default function SettingsLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, isSuperAdmin } = useAuthStore();
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const menuItems: MenuItem[] = useMemo(() => {
    const items: MenuItem[] = [];

    // General section
    items.push({
      key: 'general',
      type: 'group',
      label: 'General',
      children: [
        {
          key: '/settings/appearance',
          icon: <BgColorsOutlined />,
          label: 'Appearance',
        },
      ],
    });

    // Content section (permissions use colon notation)
    const contentItems: MenuItem[] = [];
    // Business Domains - top-level organizational groupings
    if (hasPermission('categories:read') || isSuperAdmin()) {
      contentItems.push({
        key: '/settings/domains',
        icon: <FolderOutlined />,
        label: 'Business Domains',
      });
    }
    if (hasPermission('categories:read') || isSuperAdmin()) {
      contentItems.push({
        key: '/settings/categories',
        icon: <AppstoreOutlined />,
        label: 'Categories',
      });
    }
    if (hasPermission('tags:read') || isSuperAdmin()) {
      contentItems.push({
        key: '/settings/tags',
        icon: <TagsOutlined />,
        label: 'Tags',
      });
    }
    if (hasPermission('settings:write') || isSuperAdmin()) {
      contentItems.push({
        key: '/settings/statuses',
        icon: <CheckSquareOutlined />,
        label: 'Task Statuses',
      });
    }
    if (contentItems.length > 0) {
      items.push({
        key: 'content',
        type: 'group',
        label: 'Content',
        children: contentItems,
      });
    }

    // Organization section
    const orgItems: MenuItem[] = [];
    if (hasPermission('users:read') || isSuperAdmin()) {
      orgItems.push({
        key: '/settings/users',
        icon: <TeamOutlined />,
        label: 'Users',
      });
    }
    if (hasPermission('templates:write') || isSuperAdmin()) {
      orgItems.push({
        key: '/settings/odoo',
        icon: <CloudDownloadOutlined />,
        label: 'Odoo Import',
      });
    }
    // AI Settings - available to admins and users with ai permission
    if (hasPermission('ai:write') || isSuperAdmin()) {
      orgItems.push({
        key: '/settings/ai',
        icon: <RobotOutlined />,
        label: 'AI Settings',
      });
    }
    // Accountability - available to managers/admins
    if (hasPermission('instances:read') || isSuperAdmin()) {
      orgItems.push({
        key: '/settings/accountability',
        icon: <BarChartOutlined />,
        label: 'Accountability',
      });
    }
    // Activity Log - available to admins
    if (isSuperAdmin()) {
      orgItems.push({
        key: '/settings/activity',
        icon: <HistoryOutlined />,
        label: 'Activity Log',
      });
      orgItems.push({
        key: '/settings/global-users',
        icon: <CrownOutlined />,
        label: 'Global Users',
      });
    }
    if (orgItems.length > 0) {
      items.push({
        key: 'organization',
        type: 'group',
        label: 'Organization',
        children: orgItems,
      });
    }

    // Admin section (temporarily accessible to all users for debugging)
    // TODO: Restore isSuperAdmin check after fixing auth
    items.push({
      key: 'admin',
      type: 'group',
      label: 'System',
      children: [
        {
          key: '/settings/admin',
          icon: <CrownOutlined />,
          label: 'System Admin',
        },
      ],
    });

    return items;
  }, [hasPermission, isSuperAdmin]);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key.startsWith('/')) {
      navigate(key);
    }
  };

  const getSelectedKey = () => {
    // Match exact path or closest parent
    const path = location.pathname;
    return [path];
  };

  return (
    <div style={{ display: 'flex', minHeight: 'calc(100vh - 120px)', margin: -16 }}>
      {/* Responsive styles */}
      <style>{`
        .settings-sidebar {
          width: 210px;
          min-width: 210px;
          flex-shrink: 0;
        }
        .settings-content {
          flex: 1;
          min-width: 0;
        }
        @media (max-width: 768px) {
          .settings-sidebar {
            display: none;
          }
          .settings-mobile-nav {
            display: block !important;
          }
        }
        /* Blueprint nav overrides */
        .bp-settings-menu.ant-menu-inline .ant-menu-item {
          border-radius: 0 !important;
          margin: 0 !important;
          width: 100% !important;
          height: 34px !important;
          line-height: 34px !important;
          padding-left: 16px !important;
          font-size: 13px;
          color: var(--text-secondary);
        }
        .bp-settings-menu.ant-menu-inline .ant-menu-item:hover {
          background: var(--hover-bg) !important;
          color: var(--text-primary) !important;
        }
        .bp-settings-menu.ant-menu-inline .ant-menu-item-selected {
          background: color-mix(in srgb, var(--accent-ink) 6%, var(--surface-1)) !important;
          color: var(--accent-ink) !important;
          font-weight: 500;
          border-left: 2px solid var(--accent-ink);
        }
        .bp-settings-menu .ant-menu-item-group-title {
          font-family: var(--font-mono, 'IBM Plex Mono', monospace) !important;
          font-size: 9px !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.1em !important;
          color: var(--text-tertiary) !important;
          padding: 16px 16px 4px !important;
        }
        /* First group: reduce top padding */
        .bp-settings-menu .ant-menu-item-group:first-child .ant-menu-item-group-title {
          padding-top: 8px !important;
        }
      `}</style>

      {/* Sidebar Navigation */}
      <div
        className="settings-sidebar"
        style={{
          background: 'var(--surface-1)',
          borderRight: '1px solid var(--border-default)',
        }}
      >
        {/* Settings header */}
        <div style={{
          padding: '14px 16px 12px',
          borderBottom: '1px solid var(--border-subtle)',
        }}>
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: 'var(--text-primary)',
          }}>
            SETTINGS
          </span>
        </div>
        <Menu
          className="bp-settings-menu"
          mode="inline"
          selectedKeys={getSelectedKey()}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ border: 'none', background: 'transparent' }}
        />
      </div>

      {/* Mobile Navigation (horizontal) */}
      <div className="settings-mobile-nav" style={{ display: 'none' }}>
        <Menu
          mode="horizontal"
          selectedKeys={getSelectedKey()}
          items={menuItems.flatMap((item: any) =>
            item.children?.map((child: any) => ({
              key: child.key,
              icon: child.icon,
              label: child.label,
            })) || []
          )}
          onClick={handleMenuClick}
          style={{ marginBottom: 16, borderBottom: '1px solid var(--border-default)' }}
        />
      </div>

      {/* Content Area */}
      <div
        className="settings-content"
        style={{
          padding: 24,
          background: colorBgContainer,
          overflow: 'auto',
        }}
      >
        <Outlet />
      </div>
    </div>
  );
}
